import { useEffect, useState } from 'react';
import { Badge, Button, Input, SidePanel, useToast } from '../ui';
import {
  briefOf,
  denyRow,
  DENY_SCOPES,
  normalizeKeyword,
  removeRule,
  type ContentQueueRow,
  type DenyScope,
  type SweepRule,
} from '../../lib/keywordSweep';

/**
 * Deny a proposal — or, with no row, just read the client's deny rules.
 *
 * A deny is a rule the next sweep reads, so the panel asks for what a rule needs: why (one line a
 * person can later disagree with) and how wide (this keyword, the topic, or a phrase the client never
 * wants). The pattern is editable because the agent's topic is a guess and a person knows better.
 */

interface DenyPanelProps {
  open: boolean;
  /** The proposal being denied. Null opens the rule list on its own. */
  row: ContentQueueRow | null;
  clientName: string;
  rules: SweepRule[];
  userId: string | null;
  onClose: () => void;
  onDenied: (row: ContentQueueRow) => void;
  onRulesChanged: () => void | Promise<void>;
}

const SCOPE_ORDER: DenyScope[] = ['keyword', 'topic', 'client_never'];

const wordCount = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;

function defaultPatterns(row: ContentQueueRow | null): Record<DenyScope, string> {
  if (!row) return { keyword: '', topic: '', client_never: '' };
  const brief = briefOf(row);
  const keyword = row.target_keyword ?? brief.keyword ?? row.title;
  return {
    keyword,
    topic: brief.topic ?? brief.cluster?.[0] ?? keyword,
    // Starts as the keyword; the person trims it down to the phrase that should never come back.
    client_never: keyword,
  };
}

const scopeLabel = (scope: string) => DENY_SCOPES[scope as DenyScope]?.label ?? scope;

export default function DenyPanel({ open, row, clientName, rules, userId, onClose, onDenied, onRulesChanged }: DenyPanelProps) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [scope, setScope] = useState<DenyScope>('keyword');
  const [patterns, setPatterns] = useState<Record<DenyScope, string>>(() => defaultPatterns(row));
  const [saving, setSaving] = useState(false);
  const [confirmingRule, setConfirmingRule] = useState<string | null>(null);
  const [removingRule, setRemovingRule] = useState<string | null>(null);

  // Every open starts clean: a half-typed reason for one row must not carry over to the next.
  useEffect(() => {
    if (!open) return;
    setReason('');
    setScope('keyword');
    setPatterns(defaultPatterns(row));
    setConfirmingRule(null);
  }, [open, row]);

  const reasonOk = wordCount(reason) >= 3;
  const normalized = normalizeKeyword(patterns[scope]);
  const canSave = !!row && reasonOk && !!normalized && !saving;

  const save = async () => {
    if (!row || !canSave) return;
    setSaving(true);
    try {
      const result = await denyRow({ row, reason, scope, pattern: patterns[scope], userId });
      toast.success('Denied', `${DENY_SCOPES[scope].label}: “${result.rule.pattern}”. The next sweep skips it.`);
      onDenied(result.row);
      await onRulesChanged();
      onClose();
    } catch (err) {
      toast.error('Couldn’t deny it', err instanceof Error ? err.message : 'Nothing was saved.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (rule: SweepRule) => {
    setRemovingRule(rule.id);
    try {
      await removeRule(rule.id);
      toast.success('Rule removed', `“${rule.pattern}” can be proposed again.`);
      setConfirmingRule(null);
      await onRulesChanged();
    } catch (err) {
      toast.error('Couldn’t remove the rule', err instanceof Error ? err.message : undefined);
    } finally {
      setRemovingRule(null);
    }
  };

  const brief = row ? briefOf(row) : null;

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      width={520}
      eyebrow={`Content Queue · ${clientName}`}
      title={row ? 'Deny proposal' : 'Deny rules'}
      footer={
        row ? (
          <>
            <Button variant="danger" icon="x" onClick={save} disabled={!canSave}>
              {saving ? 'Saving...' : 'Deny'}
            </Button>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        )
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
        {row ? (
          <>
            <div>
              <div style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'], color: 'var(--text-body)' }}>
                {row.title}
              </div>
              <div className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginTop: '2px' }}>
                {row.target_keyword ?? brief?.keyword ?? 'no target keyword'}
              </div>
              {brief?.pitch ? (
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', margin: '8px 0 0', lineHeight: 1.5 }}>{brief.pitch}</p>
              ) : null}
            </div>

            <div>
              <Input
                label="Why not"
                placeholder="We don't do commercial work there"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                autoFocus
              />
              <div style={{ fontSize: 'var(--text-2xs)', color: reasonOk ? 'var(--text-faint)' : 'var(--status-warn)', marginTop: '5px' }}>
                {reasonOk ? 'The next sweep reads this.' : 'One line, at least three words — the next sweep reads it.'}
              </div>
            </div>

            <div>
              <div className="t-eyebrow" style={{ marginBottom: '7px' }}>How wide</div>
              <div role="radiogroup" aria-label="Deny scope" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {SCOPE_ORDER.map((s) => {
                  const checked = scope === s;
                  return (
                    <div
                      key={s}
                      style={{
                        border: `1px solid ${checked ? 'var(--accent)' : 'var(--border-hairline)'}`,
                        background: checked ? 'var(--accent-soft)' : 'var(--surface-card)',
                        borderRadius: 'var(--radius-md)',
                        padding: '8px 10px 10px',
                      }}
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        className="pressable"
                        onClick={() => setScope(s)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          width: '100%',
                          padding: '0 0 6px',
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontFamily: 'var(--font-ui)',
                          fontSize: 'var(--text-sm)',
                          color: checked ? 'var(--accent-text)' : 'var(--text-body)',
                        }}
                      >
                        <span
                          aria-hidden
                          style={{
                            width: '12px',
                            height: '12px',
                            borderRadius: '50%',
                            border: `1px solid ${checked ? 'var(--accent)' : 'var(--border-strong)'}`,
                            boxShadow: checked ? 'inset 0 0 0 3px var(--surface-card)' : 'none',
                            background: checked ? 'var(--accent)' : 'transparent',
                            flexShrink: 0,
                          }}
                        />
                        {DENY_SCOPES[s].label}
                      </button>
                      <Input
                        fieldSize="sm"
                        aria-label={`${DENY_SCOPES[s].label} — what the rule matches`}
                        value={patterns[s]}
                        onFocus={() => setScope(s)}
                        onChange={(e) => {
                          const value = e.target.value;
                          setPatterns((p) => ({ ...p, [s]: value }));
                        }}
                      />
                      {checked ? (
                        <div style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-muted)', marginTop: '6px' }}>
                          {normalized ? DENY_SCOPES[s].blurb(normalized) : 'The rule needs a keyword or phrase to match.'}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        ) : null}

        <div>
          <div className="t-eyebrow" style={{ marginBottom: '4px' }}>Rules for {clientName}</div>
          <div style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)', marginBottom: '8px' }}>
            Removing a rule means it stops applying on the next sweep; nothing already denied comes back by itself.
          </div>
          {rules.length === 0 ? (
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', padding: '10px 0' }}>No deny rules yet.</div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column' }}>
              {rules.map((rule, i) => (
                <li
                  key={rule.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '8px 0',
                    borderBottom: i < rules.length - 1 ? '1px solid var(--border-hairline)' : 'none',
                  }}
                >
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                      <Badge tone={rule.scope === 'client_never' ? 'down' : rule.scope === 'topic' ? 'warn' : 'neutral'}>{scopeLabel(rule.scope)}</Badge>
                      <span className="t-num" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {rule.pattern}
                      </span>
                    </span>
                    <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)', marginTop: '3px' }}>{rule.reason}</span>
                  </span>
                  {confirmingRule === rule.id ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flexShrink: 0, fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                      Remove?
                      <Button size="sm" variant="danger" onClick={() => remove(rule)} disabled={removingRule === rule.id} style={{ height: '24px', padding: '0 8px' }}>
                        {removingRule === rule.id ? '...' : 'Yes'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmingRule(null)} disabled={removingRule === rule.id} style={{ height: '24px', padding: '0 8px' }}>
                        No
                      </Button>
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      icon="trash-2"
                      aria-label={`Remove the rule “${rule.pattern}”`}
                      title="Remove rule"
                      onClick={() => setConfirmingRule(rule.id)}
                      style={{ height: '24px', padding: '0 6px', flexShrink: 0 }}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </SidePanel>
  );
}
